import React, { useEffect, useMemo } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { CheckboxWithLabel } from "formik-mui";
import { Button, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { getRoleIds } from "../../utils/roles";
import {
  organizationalRoleNames,
  learningManagementRoleNames
} from "../../constants/index";
import { useCustomer } from "../../hooks/customer";
import usePermissions from "../../redux/selectors/usePermissions";
import { Tooltip } from "../../components/Tooltip";

const useStyles = makeStyles(theme => {
  return {
    formControl: {
      display: "flex",
      marginRight: 0,
      minWidth: 200
    }
  };
});

const findCheckboxIndex = (checkboxes, roleId) =>
  checkboxes.findIndex(val => Object.keys(val)[0] === roleId);

const PositionRoleCheckbox = ({ name, role, disabled }) => {
  return (
    <Field
      component={CheckboxWithLabel}
      componentsProps={{
        typography: {
          variant: "subtitle1"
        }
      }}
      name={name}
      inputProps={{
        "data-qa": `position-role-checkbox-${role.id}`
      }}
      Label={{
        label: role.label
      }}
      color="primary"
      disabled={disabled}
      type="checkbox"
    />
  );
};

const PositionRolesCheckboxList = ({
  header,
  roles,
  roleIds,
  isAdminRoleChecked,
  isSubmitting,
  disabled
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" flex={1} pb={1.25} pt={2} pr={3}>
      <Typography variant="h6" sx={{ pb: 2.5 }}>
        {header}
      </Typography>
      {roles.map(role => {
        const isCustomRole = role.id === roleIds.customRole;
        const isManageEmployeesRole = role.id === roleIds.manageEmployees;

        const disableRole =
          (isCustomRole || isManageEmployeesRole) && isAdminRoleChecked;
        return (
          <Box className={classes.formControl} key={role.id}>
            {disableRole ? (
              <Tooltip
                title={
                  <Typography color="textPrimary" variant="body2">
                    {t("administration.positionRoleDisabledTooltip")}
                  </Typography>
                }
              >
                <div>
                  <PositionRoleCheckbox
                    name={`roles[${role.index}][${role.id}]`}
                    role={role}
                    disabled={isSubmitting || disableRole || disabled}
                  />
                </div>
              </Tooltip>
            ) : (
              <PositionRoleCheckbox
                name={`roles[${role.index}][${role.id}]`}
                role={role}
                disabled={
                  isSubmitting ||
                  (isCustomRole && isAdminRoleChecked) ||
                  disabled
                }
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const PositionRolesCheckboxes = ({
  roles,
  roleIds,
  silver,
  validate,
  disabled
}) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const { t } = useTranslation();

  const adminRoleCheckboxIndex = findCheckboxIndex(
    values.roles,
    roleIds.adminConsole
  );
  const customRoleCheckboxIndex = findCheckboxIndex(
    values.roles,
    roleIds.customRole
  );
  const manageEmployeesCheckboxIndex = findCheckboxIndex(
    values.roles,
    roleIds.manageEmployees
  );
  const isAdminRoleChecked =
    values.roles[adminRoleCheckboxIndex] &&
    values.roles[adminRoleCheckboxIndex][roleIds.adminConsole];

  useEffect(() => {
    if (isAdminRoleChecked) {
      setFieldValue(
        `roles[${customRoleCheckboxIndex}][${roleIds.customRole}]`,
        false
      );
      setFieldValue(
        `roles[${manageEmployeesCheckboxIndex}][${roleIds.manageEmployees}]`,
        false
      );
    }
  }, [values]);

  const organizationalRoles = useMemo(
    () => roles.filter(({ name }) => organizationalRoleNames.includes(name)),
    [roles]
  );

  const learningManagementRoles = useMemo(
    () =>
      roles.filter(({ name }) => learningManagementRoleNames.includes(name)),
    [roles]
  );

  return (
    <Box display="flex" flexWrap="wrap">
      <PositionRolesCheckboxList
        roles={organizationalRoles}
        roleIds={roleIds}
        header={t("administration.organizationalRoles")}
        isSubmitting={isSubmitting}
        disabled={disabled}
        isAdminRoleChecked={isAdminRoleChecked}
      />
      {!(silver || validate) && (
        <PositionRolesCheckboxList
          roles={learningManagementRoles}
          roleIds={roleIds}
          header={t("administration.learningManagementRoles")}
          isSubmitting={isSubmitting}
          disabled={disabled}
          isAdminRoleChecked={isAdminRoleChecked}
        />
      )}
    </Box>
  );
};

const PositionRoles = ({ allRoles, positionRoles, onUpdateRoles }) => {
  const { t } = useTranslation();

  const roleIds = getRoleIds(allRoles);

  const { customRoleEnabled } = useCustomer();
  const { silver, validate } = usePermissions();
  const displayedRoles = allRoles
    ? allRoles.filter(
        role =>
          !(
            (role.id === roleIds.customRole && !customRoleEnabled) ||
            (role.id === roleIds.manageEmployees && silver)
          )
      )
    : [];

  const initialValues = {
    roles: displayedRoles.map(role => ({
      [role.id]: !!positionRoles.find(
        positionRole => positionRole.id === role.id
      )
    }))
  };

  const roleCheckboxes = displayedRoles.map((role, index) => ({
    index,
    ...role
  }));

  return (
    allRoles && (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={({ roles }) => onUpdateRoles(roles)}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Box display="flex" flexDirection="column">
                {silver || validate ? (
                  <Tooltip
                    title={
                      <Typography color="textPrimary" variant="body2">
                        {t("administration.goldPlatinumFeatureTooltip")}
                      </Typography>
                    }
                    data-qa="position-roles-disabled-tooltip"
                  >
                    <div>
                      <PositionRolesCheckboxes
                        disabled={true}
                        roles={roleCheckboxes}
                        roleIds={roleIds}
                        silver={silver}
                        validate={validate}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <PositionRolesCheckboxes
                    disabled={isSubmitting}
                    roles={roleCheckboxes}
                    roleIds={roleIds}
                    silver={silver}
                    validate={validate}
                  />
                )}
                {!(silver || validate) && (
                  <Box
                    flex={1}
                    display="flex"
                    justifyContent="flex-end"
                    pt={{ xs: 2, md: 0 }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                      data-qa="save-position-roles-btn"
                    >
                      {t("administration.saveChangesBtn")}
                    </Button>
                  </Box>
                )}
              </Box>
            </Form>
          );
        }}
      </Formik>
    )
  );
};

export default PositionRoles;

PositionRoles.defaultProps = {
  positionRoles: []
};
